import React from "react";
import { addDays, startOfDay } from "date-fns";
import { Box, IconButton, Typography } from "@mui/material";
import SectionCollapsible from "../../../../common/SectionCollapsible";
import Outcome from "./Outcome";
import AddIcon from "@mui/icons-material/Add";
import { useFormikContext } from "formik";
import { AddSolutionFormValues } from "../../../types";
import {
  isUnitOfSaleOutcomeFilledOut,
  isUnitOfSaleSelected,
  outcomesTouched,
} from "../../../utils";

function OutcomesContainer({ open }: { open: boolean }) {
  const context = useFormikContext<AddSolutionFormValues>();
  const outcomes = context.values.outcomes;
  const hasLegacyEpochSavedOutcome = outcomes.some(
    (outcome) =>
      outcome.origin === "saved" && outcome["outcome-start-date"] === 0,
  );

  const [outcomesNo, setOutcomesNo] = React.useState<number>(
    outcomes.length === 0
      ? 1
      : Math.max(...outcomes.map((outcome) => outcome.outcomeCounter)),
  );

  const sortedOutcomes = [...outcomes].sort((a, b) =>
    a.outcomeCounter > b.outcomeCounter ? 1 : -1,
  );

  const latestOutcome = sortedOutcomes[sortedOutcomes.length - 1];

  const generateNewContainer = () => {
    const containerTracker = outcomesNo + 1;
    const newOutcomeStartDate = latestOutcome?.["outcome-end-date"]
      ? startOfDay(
          addDays(new Date(latestOutcome["outcome-end-date"]), 1),
        ).getTime()
      : null;

    setOutcomesNo(containerTracker);
    context.setFieldValue(
      "outcomes",
      outcomes.concat([
        {
          unit: "",
          value: "",
          "unit-of-sale": false,
          "outcome-start-date": newOutcomeStartDate,
          "outcome-end-date": null,
          outcomeCounter: containerTracker,
          origin: "draft" as const,
        },
      ]),
    );
  };

  return (
    <Box data-testid="add-outcomes-container">
      <SectionCollapsible open={open} label="Outcomes">
        {hasLegacyEpochSavedOutcome && (
          <Box sx={{ padding: "0 3 2" }}>
            <Typography variant="subtitle2" align="left">
              Legacy start dates
            </Typography>
            <Typography variant="body2" align="left" sx={{ mb: 2 }}>
              Some existing outcomes had no recorded start date, so they show as
              01/01/1970 until a real start date is entered.
            </Typography>
          </Box>
        )}
        {sortedOutcomes.map((outcome) => (
          <Box key={outcome.outcomeCounter} sx={{ padding: "0 3" }}>
            <Outcome value={outcome} />
          </Box>
        ))}
        {outcomesTouched(context.values) &&
          !isUnitOfSaleSelected(context.values) && (
            <Typography color="error" variant="subtitle2" align="left">
              The unit of sale must be selected.
            </Typography>
          )}
        {outcomesTouched(context.values) &&
          isUnitOfSaleSelected(context.values) &&
          !isUnitOfSaleOutcomeFilledOut(context.values) && (
            <Typography color="error" variant="subtitle2" align="left">
              The Outcome tagged as unit of sale must have a unit and a value
              selected.
            </Typography>
          )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <IconButton
            onClick={generateNewContainer}
            data-testid="add-another-outcome-button"
            sx={{
              paddingTop: 1,
              "&:hover": { backgroundColor: "transparent" },
            }}
            aria-label="add-outcome-button"
            size="large"
          >
            <AddIcon />
            <Typography variant="body2">Add another</Typography>
          </IconButton>
        </Box>
      </SectionCollapsible>
    </Box>
  );
}

export default OutcomesContainer;
